/* prettier-ignore */

import { computed, effect, inject, Inject, Injectable, NgZone, signal } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { RestService } from "./rest.service";
import { TranslationService } from "./translation.service";
import { BehaviorSubject, combineLatest, filter, firstValueFrom, Observable, take } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { Router } from "@angular/router";
import { StandardDialog } from "../shared/dialogs";
import { MatBottomSheetService, MatDialogService } from 'src/app/services';
import { InformerMode } from "../interfaces/informer-mode.interface";
import { isVariantLoadError } from "../shared/image-variants/helpers/image-variants";

@Injectable({
  providedIn: "root",
})

export class ToolsService {
  private _router = inject(Router);
  private _matDialogService = inject(MatDialogService);
  private _matBottomSheet = inject(MatBottomSheetService);
  private _ngZone = inject(NgZone);

  private isAppSubject = new BehaviorSubject<boolean | null>(null);
  private isDistributorSubject = new BehaviorSubject<boolean | null>(null);
  isApp$ = this.isAppSubject.asObservable();
  isDistributor$ = this.isDistributorSubject.asObservable();

  private commonAppReadySubject = new BehaviorSubject<boolean>(false);
  commonAppReady$ = this.commonAppReadySubject.asObservable();

  ttlTwoHours: number = 1000 * 3600 * 2; //2horas
  homeDistributor: any = null;
  menuIsShown: boolean = false;
  showMenuDropdown: any;

  isPreProduction = signal(false);
  isCorporativeColorsLoaded = signal(false);
  isAppLoaded = signal(false);
  isTechEditMode = signal(false);
  // HRS-2990: modo del informador. Fuente de verdad única de los tres flujos
  // (`simple` / `complete` / `predefined-route`); `null` = visitante normal.
  informerMode = signal<InformerMode | null>(null);
  // Derivado del modo: "modo técnico" del código histórico significaba, en la
  // práctica, "estoy creando una ruta predefinida". No se escribe directamente:
  // usar `informerMode.set(...)`.
  isTechMode = computed(() => this.informerMode() === 'predefined-route');
  // HRS-2990: registro configurado que pide el acceso directo del panel en
  // `?register=`. Decide los campos modulares del formulario del informador.
  informerRegisterId = signal<string | null>(null);
  isRegisterMode = signal(false);
  isInfoRoutePage = signal(false);
  isPlannerMode = signal(false);
  isFooter = signal(false);

  canUseRoutes = signal(true);
  isWebApp = signal(false);
  isHikingStandalone = signal(false);
  chatType = signal('text');
  enabledInstallBanner = signal(true);
  showRouteSpinner = signal(false);
  showHeader = signal(false);
  rrssUrl = signal<string | undefined>(undefined);

  public showSendRouteButton = signal(true);
  public showShareRouteButton = signal(false);
  public showRatingsButton = signal(true);
  // HRS-2513: does this tenant have the planner/route flow enabled? (CMS flag showPlannerButton).
  // Gates the "make a route" CTA icon in the chat header, like capabilities.voice gates the mic.
  public showPlannerButton = signal(false);

  /**
   * HRS-2579: bloque `maxRouteRadius` de getCommonApp — si esta app pregunta por el
   * radio total de la ruta y con qué valor sale marcado. `null` mientras el CMS no
   * ha contestado; `defaultKm` a null es "sin límite", que no es lo mismo que no
   * saberlo todavía.
   */
  public maxRouteRadius = signal<{ enabled: boolean; defaultKm: number | null } | null>(null);
  
  setHeaderHeightVariable = effect(() => {
    const root = this.document.querySelector(':root') as HTMLElement;
    root?.style.setProperty('--header-height', this.isPlannerMode() ? '0px' : '64px');
  });

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
    private titleService: Title,
    private _translationService: TranslationService,
    private rest: RestService
  ) {}

  public waitUntilReady(): Promise<[boolean | null, boolean | null]> {
    return firstValueFrom(
      combineLatest([this.isApp$, this.isDistributor$]).pipe(
        filter(([isApp, isDistributor]) => isApp !== null || isDistributor !== null),
        take(1),
      )
    );
  }

  enableSpinner(){
    this.showRouteSpinner.update(prev => true)
  }

  disableSpinner(){
    this.showRouteSpinner.update(prev => false)
  }

  enableHeader(){
    this.showHeader.update(prev => true);
  }

  disableHeader(){
    this.showHeader.update(prev => false);
  }

  setIsApp(value: boolean) {
    // this.setAppStarted();
    this.isAppSubject.next(value);
  }

  setIsDistributor(value: boolean) {
    // this.setAppStarted();
    this.isDistributorSubject.next(value);
  }

  setCommonAppReady(){
    this.commonAppReadySubject.next(true);
  }

  setAppStarted(){
    this.setCorporativeColors();
    this.enableHeader();
    this.isAppLoaded.set(true)
  }

  setCorporativeColors(){
    this.setCorporiveColors();
    this.changeThemeColor(this.rest.device()?.style?.colorCorp1 || "#000");
    this.isCorporativeColorsLoaded.update(prev => true);
  }

  get isApp(): boolean | null {
    return this.isAppSubject.value;
  }

  get isDistributor(): boolean | null {
    return this.isDistributorSubject.value;
  }

  getDistributorUrl(name) {
    let url = new URL(window.location.href);
    let parts = url.hostname.split(".");
    parts.shift();
    url.hostname = parts.join(".");
    url.hostname = `${name}.${url.hostname}`;
    url.pathname = url.pathname.substring(0, url.pathname.lastIndexOf("/"));
    url.search = "";
    return url.toString();
  }

  goBack(){
    this._matDialogService.closeAllMatDialogs();
    this._matBottomSheet.closeAllMatBottomSheets();
    
    /**
     * A setTimeout is neeeded to clean the dialogs and bottom sheets before the
     * goBack dialog appears. Otherwise the dialog is positioned incorrectly.
     */
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this._ngZone.run(() => {
          this.openConfirmationGoBackDialog();
        });
      }, 400);
    });
  }

  async openConfirmationGoBackDialog(): Promise<void> {
    await this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        iconName: 'alert',
        title: '¿' + this._translationService.instant('Quieres volver atrás') + '?',
        content: this._translationService.instant('Si regresas, perderás todos los cambios que hiciste en tu ruta. ¿Seguro que deseas continuar?'),
        primaryActionButtonText: this._translationService.instant('Sí, volver'),
        secondaryActionButtonText: this._translationService.instant('No, seguir aquí'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeAllMatDialogs();
          this._router.navigateByUrl('/home');
        },
        handleClickSecondaryButton: () => {
          this._matDialogService.closeMatDialog(this._matDialogService.currentDialogRef());
        }
      }
    });
  }

  public async openPassportGuide(): Promise<void> {
    // HRS-3045: ToolsService es root (lo inyecta AppComponent); el import estático del
    // diálogo metía la guía del pasaporte en 14 idiomas (~108 KB raw) en el bundle inicial.
    const { PassportGuideDialogComponent } = await import(
      '../shared/header/components/user/passport-guide-dialog/passport-guide-dialog.component'
    );
    await this._matDialogService.openMatDialog({
      component: PassportGuideDialogComponent,
      panelClass: 'passport-guide-dialog',
      enterAnimationDuration: '200ms',
      exitAnimationDuration: '150ms',
    });
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //------------------------------------------------------------ TEMP LOCAL STORAGE ------------------------------------------------------------------  
  setWithExpiry(key, value, ttl = this.ttlTwoHours) {
    const now = new Date();
    const item = { value: value, expiry: now.getTime() + ttl };
    localStorage.setItem(key, JSON.stringify(item));
  }
  getWithExpiry(key) {
    const itemStr = localStorage?.getItem(key);
    if (!itemStr) 
      return null;
    
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- SLUGFUNCTION ---------------------------------------------------------------------
  slugify(str: string) {
    const slugString = String(str)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
    
    if(slugString.trim() === '')
        return;

    return slugString;
  }

  public async onShareClick(title: string, url?: string): Promise<boolean> {
    const shareUrl = url ?? (typeof window !== 'undefined' ? window.location.href : '');
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await (navigator as Navigator).share({ title, url: shareUrl });
        return true;
      } catch (err) {
        // The user dismissing the native share sheet is a successful no-op — don't
        // pop the fallback panel on top of it.
        if (err instanceof DOMException && err.name === 'AbortError') return true;
        // Any other failure (notably navigator.share rejecting inside an iframe that
        // lacks allow="web-share", as in the embed) must return false so the caller
        // falls back to the social-networks panel instead of silently doing nothing.
        return false;
      }
    }

    return false;
  }

  // slugify(value: string): string {
  //   const normalized = (value ?? 'item')
  //     .toString()
  //     .toLowerCase()
  //     .normalize('NFD')
  //     .replace(/[\u0300-\u036f]/g, '')
  //     .replace(/[^a-z0-9]+/g, '-')
  //     .replace(/(^-|-$)+/g, '');

  //   return normalized || 'item';
  // }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- ERROR IMAGEN ----------------------------------------------------------------------
  onImageError(e) {
    // HRS-3471: a failed srcset variant is retried with the original by the responsive-image directive.
    if (isVariantLoadError(e)) return;
    e.target.src = "/assets/imgs/imageDefault.png";
  }
  onImgOfferError(e) {
    e.target.style.objectFit = "contain";
    e.target.src = "/assets/imgs/offerDefault.webp";
  }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- SET SEO PAGE ---------------------------------------------------------------------
  public setMetaDescription(content: string) {
    this.meta.updateTag({ name: "description", content: content });
  }
  public setMetaTitle(title: string) {
    this.titleService.setTitle(title);
  }

  setCorporiveColors() {
    const root = document.querySelector(":root") as HTMLElement;
    root.style.setProperty("--main-color", this.rest.device()?.style?.colorCorp1 || "#00ABAF");
    root.style.setProperty("--main-color-alpha", `${this.rest.device()?.style?.colorCorp1}66` || "#00ABAF66");
    root.style.setProperty("--main-color-strong", this.rest.device()?.style?.colorCorp2 || "#08515D");
    root.style.setProperty("--header-color", this.rest.device()?.style?.headerColor || "#00ABAF");
    root.style.setProperty("--compl-color", this.rest.device()?.style?.corpCompl || "#08515D");
    root.style.setProperty("--background-color", this.rest.device()?.style?.backgroundColor || "#FFF");
    
    // New corporative colours
    root.style.setProperty("--primary", this.rest.device()?.style?.colorCorp1 || "#00ABAF");
    root.style.setProperty("--primary-dark", "color-mix(in srgb, var(--primary) 40%, black 60%)");
    root.style.setProperty("--primary-light", "color-mix(in srgb, var(--primary) 70%, white 30%)");
    root.style.setProperty("--secondary", this.rest.device()?.style?.colorCorp2 || "#08515D");
    root.style.setProperty("--secondary-dark", "color-mix(in srgb, var(--secondary) 40%, black 60%)");
    root.style.setProperty("--secondary-light", "color-mix(in srgb, var(--secondary) 70%, white 30%)");

    this.isWebApp()
      ? root.style.setProperty("--drag-delay", '2s')
      : root.style.setProperty("--drag-delay", '0.15s')
  }

  getLocation() {
    return new Observable((observer) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => observer.next(position.coords),
          (error) => observer.error(error),
          { enableHighAccuracy: true }
        );
      } else observer.error("La geolocalización no está disponible en este navegador.");
    });
  }

  getSubdomain(url) {
    let domain = url;
    if (url.includes("://")) domain = url.split("://")[1];
    const subdomain = domain.split(".");
    let urlSegments = 2;
    if (domain.includes("localhost")) 
      urlSegments = 1;
  
    return subdomain.length > urlSegments ? subdomain[0] : null;
  }

  changeThemeColor(color: string) {
    this.document.querySelector('meta[name="theme-color"]').setAttribute("content", color);
  }

  toggleMenu(event: any) {
    event.stopPropagation();
    if (event.type === "ionScrollStart" && !this.menuIsShown) return;
    this.menuIsShown = !this.menuIsShown;
  }

  //------------------------------------------ AUXILIAR DISTANCE FUNCTIONS ------------------------------------------------------------
    sortItemsPerDistance(items, location) {
      return items?.map((item) => {
        item.distance = this.getDistanceBetween(parseFloat(location.latitude), parseFloat(location.longitude), parseFloat(item.latitude), parseFloat(item.longitude));
        item.distanceM = item.distance >= 1000 ? (item.distance / 1000).toFixed(2) + " Km's" : item.distance.toFixed(0) + " M's";
        return item;
      }).sort((a, b) => a.distance - b.distance);
    }
  
    getDistanceBetween(lat1: any, lon1: any, lat2: any, lon2: any): any {
      const rad = function (x) { return (x * Math.PI) / 180 };
      const R = 6378.137;
      const dLat = rad(lat2 - lat1);
      const dLong = rad(lon2 - lon1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +  Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const d = R * c * 1000;
      return d;
    }
  
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- Check route ---------------------------------------------------------------------
  isRoutePage(route: string): boolean {
    const currentUrl = this._router.url;
    return currentUrl.includes(route);
  }

  isNonEmptyObject(value: any){
    return value !== null && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;
  }
}